// Template service for API-based template storage
import { ReportFormData } from "../types/ReportFormData";
import { ReportTemplate } from "../types/ReportTemplate";
import { ScreenGroup } from "../types/ScreenGroup";
import { apiClient } from "./apiClient";

interface ApiTemplate {
	template_id: string;
	template_name: string;
	description: string | null;
	params: any;
	created_at: string;
	updated_at: string;
}

/**
 * Serializes ReportFormData for API requests
 * Converts Date objects to ISO strings
 */
const serializeFormData = (data: ReportFormData): any => {
	return {
		...data,
		start_date: data.start_date ? data.start_date.toISOString() : null,
		end_date: data.end_date ? data.end_date.toISOString() : null,
		specific_dates_to_exclude: data.specific_dates_to_exclude.map((date) =>
			date.toISOString()
		),
	};
};

/**
 * Deserializes ReportFormData from API responses
 * Converts ISO strings back to Date objects
 */
const deserializeFormData = (data: any): ReportFormData => {
	// Handle templates that store a single screen_groups array (BE format)
	// by splitting into renames (1 screen) and groups (2+ screens).
	let renames: ScreenGroup[] = data?.screen_renames ?? [];
	let groups: ScreenGroup[] = data?.screen_groups ?? [];

	if (renames.length === 0 && groups.length === 0 && Array.isArray(data?.screen_groups)) {
		renames = (data.screen_groups as ScreenGroup[]).filter(
			(g) => g.screen_ids?.length === 1,
		);
		groups = (data.screen_groups as ScreenGroup[]).filter(
			(g) => g.screen_ids?.length >= 2,
		);
	}

	return {
		report_title: data?.report_title ?? "",
		client_name: data?.client_name ?? "",
		campaign_name: data?.campaign_name ?? "",
		included_media_IDs: data?.included_media_IDs ?? [],
		excluded_screen_IDs: data?.excluded_screen_IDs ?? [],
		start_date: data.start_date ? new Date(data.start_date) : null,
		end_date: data.end_date ? new Date(data.end_date) : null,
		days: data?.days ?? [],
		exclude_holidays: data?.exclude_holidays ?? false,
		specific_dates_to_exclude: (data?.specific_dates_to_exclude ?? []).map(
			(dateStr: string) => new Date(dateStr)
		),
		daily_working_hours: data?.daily_working_hours ?? [],
		offline_logs: data?.offline_logs ?? [],
		screen_renames: renames,
		screen_groups: groups,
	};
};

/**
 * Deserializes a template from API response
 */
const deserializeTemplate = (template: ApiTemplate): ReportTemplate => {
	return {
		id: template.template_id,
		name: template.template_name,
		data: deserializeFormData(template.params),
		createdAt: template.created_at,
		lastUsedAt: template.updated_at,
	};
};

/**
 * Get all templates from API
 */
export const getAllTemplates = async (): Promise<ReportTemplate[]> => {
	try {
		const response = await apiClient.get<ApiTemplate[]>("/templates");
		return response.data.map(deserializeTemplate);
	} catch (error) {
		console.error("Error fetching templates:", error);
		throw error;
	}
};

/**
 * Get a specific template by ID
 */
export const getTemplate = async (id: string): Promise<ReportTemplate> => {
	try {
		const response = await apiClient.get<ApiTemplate>(`/templates/${id}`);
		return deserializeTemplate(response.data);
	} catch (error) {
		console.error(`Error fetching template ${id}:`, error);
		throw error;
	}
};

/**
 * Create a new template
 */
export const createTemplate = async (
	name: string,
	formData: ReportFormData
): Promise<ReportTemplate> => {
	try {
		const serializedData = serializeFormData(formData);
		const response = await apiClient.post<ApiTemplate>("/templates", {
			template_name: name,
			params: serializedData,
		});
		return deserializeTemplate(response.data);
	} catch (error) {
		console.error("Error creating template:", error);
		throw error;
	}
};

/**
 * Update an existing template
 */
export const updateTemplate = async (
	id: string,
	updates: Partial<ReportTemplate>
): Promise<ReportTemplate> => {
	try {
		// API requires both template_name and params in update payload.
		const currentTemplate = await getTemplate(id);
		const response = await apiClient.put<ApiTemplate>(`/templates/${id}`, {
			template_name: updates.name ?? currentTemplate.name,
			params: serializeFormData(updates.data ?? currentTemplate.data),
		});
		return deserializeTemplate(response.data);
	} catch (error) {
		console.error(`Error updating template ${id}:`, error);
		throw error;
	}
};

/**
 * Delete a template
 */
export const deleteTemplate = async (id: string): Promise<void> => {
	try {
		await apiClient.delete(`/templates/${id}`);
	} catch (error) {
		console.error(`Error deleting template ${id}:`, error);
		throw error;
	}
};
